import { AsyncPipe } from '@angular/common';
import { Component, DestroyRef, inject, OnInit } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { FormBuilder, FormControl, FormGroup, ReactiveFormsModule } from '@angular/forms';
import { MatButtonToggle, MatButtonToggleGroup } from '@angular/material/button-toggle';
import { MatOption } from '@angular/material/core';
import { MatFormField } from '@angular/material/form-field';
import { MatProgressSpinner } from '@angular/material/progress-spinner';
import { MatSelect } from '@angular/material/select';
import { AvailableLang } from '@app/core/const/i18n';
import * as fromEntities from '@app/core/entities/reducers';
import { Asset, Currency } from '@app/core/models';
import {
  MultiselectWithSearchComponent
} from '@app/shared/components/multiselect-with-search/multiselect-with-search.component';
import { SelectOption } from '@app/shared/components/multiselect-with-search/select-option';
import * as fromRoot from '@app/store/reducers';
import { TranslocoDirective, TranslocoService } from '@jsverse/transloco';
import { select, Store } from '@ngrx/store';
import { EChartsOption } from 'echarts';
import { isEmpty } from 'lodash';
import { NgxEchartsDirective } from 'ngx-echarts';
import { BehaviorSubject, combineLatest, Observable } from 'rxjs';
import { filter, map, take, tap, withLatestFrom } from 'rxjs/operators';

import { ForecastChartsService } from '../services/forecast-charts.service';

export const forcastAssetClassesKey = 'forcast-assetclasses';

@Component({
  selector: 'am-forecast',
  templateUrl: './forecast.component.html',
  styleUrls: ['./forecast.component.scss'],
  imports: [
    TranslocoDirective,
    ReactiveFormsModule,
    MatFormField,
    MatSelect,
    MatOption,
    MatButtonToggleGroup,
    MatButtonToggle,
    MultiselectWithSearchComponent,
    NgxEchartsDirective,
    MatProgressSpinner,
    AsyncPipe,
  ],
  standalone: true,
})
export class ForecastComponent implements OnInit {
  currencies$: Observable<Currency[]>;

  assetList$: Observable<Asset[]>;

  assetListOptions$: Observable<SelectOption[]>;

  forecastOptions$: Observable<EChartsOption>;

  currentLang$: Observable<AvailableLang>;

  form: FormGroup;

  private readonly _store = inject<Store<fromRoot.State>>(Store);
  private readonly _forecastChartsService = inject(ForecastChartsService);
  private readonly _translocoService = inject(TranslocoService);
  private readonly _formBuilder = inject(FormBuilder);
  private _changeForecastParamsSub = new BehaviorSubject<void>(undefined);
  private _destroyRef = inject(DestroyRef);

  ngOnInit(): void {
    const initialAssetClasses = this._getPersistedAssetClasses();
    this.form = this._formBuilder.group({
      currency: new FormControl(null),
      isHedge: new FormControl(false),
      assetClasses: new FormControl(initialAssetClasses ?? []),
    });
    this.currencies$ = this._store.pipe(
      select(fromEntities.getAllCurrencies),
      filter(currencies => currencies && currencies.length > 0),
      take(1),
      tap(currencies => {
        this.form.get('currency').setValue(currencies[0].label);
      })
    );

    this.assetList$ = this._store.select(fromEntities.getAllAssets).pipe(filter(assets => assets && assets.length > 0));

    this.assetListOptions$ = combineLatest([this.assetList$, this._store.select(fromRoot.getCurrentLang)]).pipe(
      takeUntilDestroyed(this._destroyRef),
      map(([assetList, currentLang]) =>
        assetList.map(assetClass => ({
          label: assetClass.name[currentLang],
          value: assetClass.id,
        }))
      ),
      tap(assetClassList => {
        if (initialAssetClasses == null) {
          this.form.get('assetClasses').setValue(assetClassList.map(assetClass => assetClass.value));
        }
      })
    );
    this.currentLang$ = this._translocoService.selectTranslateObject('axis-label', {}, 'forecast').pipe(
      takeUntilDestroyed(this._destroyRef),
      filter(selectTranslateObject => !isEmpty(selectTranslateObject)),
      withLatestFrom(this._store.select(fromRoot.getCurrentLang)),
      map(([_, currentLang]) => currentLang)
    );
    this.forecastOptions$ = combineLatest([
      this.form.valueChanges,
      this.currentLang$,
      this.assetList$,
      this.currencies$,
    ]).pipe(
      takeUntilDestroyed(this._destroyRef),
      tap(([{ assetClasses }]) => {
        this._persistAssetClasses(assetClasses);
      }),
      map(([{ assetClasses, currency, isHedge }, currentLang, assets]) => {
        return this._forecastChartsService.getForecastOptions(
          currency,
          isHedge,
          assets.filter(asset => assetClasses.includes(asset.id)),
          currentLang
        );
      })
    );
  }

  private _persistAssetClasses(assetClasses: number[]): void {
    localStorage.setItem(forcastAssetClassesKey, JSON.stringify(assetClasses));
  }

  private _getPersistedAssetClasses(): number[] | null {
    const initialAssetClassesString = localStorage.getItem(forcastAssetClassesKey);
    if (initialAssetClassesString == null) {
      return null;
    }
    return JSON.parse(initialAssetClassesString);
  }
}
